import { supabase } from '@cleanpro/common-ui';

// Interface for creating a new job with all required database fields
export interface JobInsertData {
  customer_id: string;
  service_id: string;
  business_id: string;
  property_id: string; // Required in database
  quote_id?: string | null;
  date_time: string;
  duration: number;
  status: string;
  price: number;
  notes?: string | null;
  is_recurring?: boolean | null;
  recurring_frequency?: 'weekly' | 'bi-weekly' | 'tri-weekly' | 'monthly' | null;
  recurring_end_date?: string | null;
  recurring_parent_id?: string | null;
}

/**
 * Fetches all jobs from the 'jobs' table in the database,
 * ordered by the 'created_at' field in descending order.
 *
 * @returns {Promise<any[]>} A promise that resolves to an array of job records.
 * @throws Will throw an error if the database query fails.
 */
export async function fetchAllJobs() {
  // Debug: Check what role the user has for RLS policy
  const { data: roleData, error: roleError } = await supabase.rpc('get_user_role');
  console.log('🔍 DEBUG - get_user_role():', roleData);
  if (roleError) {
    console.error('❌ get_user_role() error:', roleError);
  }

  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('fetchAllJobs error:', error);
    throw error;
  }

  console.log('fetchAllJobs result', { count: data?.length ?? 0 });
  return data;
}

/**
 * Adds a new job to the 'jobs' table in the database.
 *
 * @param jobData - The job data to insert with all required database fields.
 *
 * @returns The inserted job record.
 * @throws If there is an error inserting the job into the database.
 */
export async function addJob(jobData: JobInsertData) {
  const { data, error } = await supabase
    .from('jobs')
    .insert([jobData])
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data;
}
